import axios from 'axios';
import store from "@/store";
import { mapGetters } from 'vuex';

export default {
    name: 'GCFPaymentPopup',
    props: {
        // Сумма платежа
        amount: {
            type: [String, Number],
            required: true
        },
        // Валюта платежа (USD, EUR, GBP)
        currency: {
            type: String,
            default: 'USD'
        },
        // Описание платежа
        description: {
            type: String,
            default: ''
        },
        // Идентификатор заказа
        orderId: {
            type: String,
            required: true
        },
        externalid: {
            type: String,
            required: true
        },
        // ID пользователя для авторизации API запросов
        uuid: {
            type: String,
            required: true
        },
        // Язык интерфейса (rus, eng)
        language: {
            type: String,
            default: 'eng'
        },
        // Промокод (опционально)
        promoCode: {
            type: String,
            default: ''
        },
        // ID клиента в системе GCF
        customerId: {
            type: String,
            default: process.env.VUE_APP_GCF_CUSTOMER_ID || '2TX17KRH'
        },
        // ID инвойса для платежа
        invoiceId: {
            type: String,
            default: null
        },
        // Тип инвойса
        invoiceType: {
            type: String,
            default: 'tree'
        },
        // API ключ для GCF
        apiKey: {
            type: String,
            default: process.env.VUE_APP_GCF_API_KEY || ''
        },
        // Информация о плательщике
        billingInfo: {
            type: Object,
            default: () => ({
                email: '',
                address: '',
                city: '',
                postcode: '',
                country: ''
            })
        }
    },

    data() {
        return {
            // Данные карты
            cardNumber: '',
            expiryDate: '',
            cvc: '',
            cardholder: '',

            // Статусы обработки
            loading: false,
            error: null,

            // URL для 3DS
            challengeUrl: null,
            ddcUrl: null,

            // GCF объект
            sp: null,

            // Информация о платеже
            paymentCompleted: false,
            paymentResult: null,

            // Счетчик проверки статуса
            statusCheckCount: 0,
            maxStatusChecks: 10
        };
    },

    computed: {
        ...mapGetters([
            'currentLanguage',
        ]),

        // Локализованные тексты
        GCFPay() {
            return this.currentLanguage.key === 'ru'
                ? 'Оплата картой'
                : 'Card Payment';
        },

        cardNumberLabel() {
            return this.currentLanguage.key === 'ru'
                ? 'Номер карты'
                : 'Card Number';
        },

        expiryDateLabel() {
            return this.currentLanguage.key === 'ru'
                ? 'Срок действия'
                : 'Expiry Date';
        },

        cardholderNameLabel() {
            return this.currentLanguage.key === 'ru'
                ? 'Имя владельца карты'
                : 'Cardholder Name';
        },

        paymentAmount() {
            return this.currentLanguage.key === 'ru'
                ? 'Сумма платежа'
                : 'Payment Amount';
        },

        payButton() {
            return this.currentLanguage.key === 'ru'
                ? 'Оплатить'
                : 'Pay';
        },

        securePaymentInfo() {
            return this.currentLanguage.key === 'ru'
                ? 'Безопасный платеж, защищенный шифрованием'
                : 'Secure payment protected by encryption';
        },

        processingPayment() {
            return this.currentLanguage.key === 'ru'
                ? 'Обработка платежа...'
                : 'Processing payment...';
        },

        bankVerification() {
            return this.currentLanguage.key === 'ru'
                ? 'Верификация банка'
                : 'Bank Verification';
        },

        completeVerification() {
            return this.currentLanguage.key === 'ru'
                ? 'Пожалуйста, завершите проверку вашего банка'
                : 'Please complete your bank verification';
        },

        paymentSuccess() {
            return this.currentLanguage.key === 'ru'
                ? 'Платеж успешно выполнен!'
                : 'Payment Successful!';
        },

        paymentProcessed() {
            return this.currentLanguage.key === 'ru'
                ? 'Ваш платеж был успешно обработан'
                : 'Your payment has been successfully processed';
        },

        backToSite() {
            return this.currentLanguage.key === 'ru'
                ? 'Вернуться на сайт'
                : 'Back to Site';
        },

        errorInitPayment() {
            return this.currentLanguage.key === 'ru'
                ? 'Ошибка инициализации платежной системы'
                : 'Error initializing payment system';
        },

        errorLoadingPaymentSystem() {
            return this.currentLanguage.key === 'ru'
                ? 'Ошибка загрузки платежной системы'
                : 'Error loading payment system';
        },

        errorLoadingDependencies() {
            return this.currentLanguage.key === 'ru'
                ? 'Ошибка загрузки зависимостей'
                : 'Error loading dependencies';
        },

        errorProcessingPayment() {
            return this.currentLanguage.key === 'ru'
                ? 'Ошибка обработки платежа: {0}'
                : 'Error processing payment: {0}';
        },

        transactionInfoNotAvailable() {
            return this.currentLanguage.key === 'ru'
                ? 'Информация о транзакции недоступна'
                : 'Transaction information not available';
        },

        paymentFailed() {
            return this.currentLanguage.key === 'ru'
                ? 'Платеж не выполнен'
                : 'Payment failed';
        },

        errorGettingTransactionInfo() {
            return this.currentLanguage.key === 'ru'
                ? 'Ошибка получения информации о транзакции'
                : 'Error getting transaction information';
        },

        paymentSystemNotInitialized() {
            return this.currentLanguage.key === 'ru'
                ? 'Платежная система не инициализирована'
                : 'Payment system not initialized';
        },

        // Форматированная сумма для отображения
        formattedAmount() {
            return `${this.amount} ${this.currency}`;
        },

        // Проверка валидности формы
        isFormValid() {
            return (
                this.cardNumber.replace(/\s/g, '').length === 16 &&
                this.expiryDate.length === 5 &&
                this.cvc.length >= 3 &&
                this.cardholder.trim() !== ''
            );
        },

        // Получение месяца из даты срока действия
        expMonth() {
            return this.expiryDate.split('/')[0] || '';
        },

        // Получение года из даты срока действия
        expYear() {
            const yearPart = this.expiryDate.split('/')[1] || '';
            // Формат года должен быть 4-значным для GCF API
            return yearPart.length === 2 ? `20${yearPart}` : yearPart;
        }
    },

    mounted() {
        // Инициализация GCF после монтирования компонента
        this.initGCF();

        // Добавляем слушатель сообщений для iframe
        window.addEventListener('message', this.handleIframeMessage);
    },

    beforeDestroy() {
        // Удаляем слушатель при уничтожении компонента
        window.removeEventListener('message', this.handleIframeMessage);

        // Сбрасываем состояние GCF
        if (this.sp) {
            this.sp.reset();
        }

        // Очищаем интервалы
        if (this.statusCheckInterval) {
            clearInterval(this.statusCheckInterval);
        }
    },

    methods: {
        // Перевод ключей для локализации
        $t(key) {
            // Используем локализованные вычисляемые свойства
            return this[key] || key;
        },

        // Инициализация библиотеки GCF
        initGCF() {
            try {
                // Проверяем, доступен ли GCF
                if (typeof GCF === 'undefined') {
                    this.loadGCFScript();
                } else {
                    this.createGCFInstance();
                }
            } catch (error) {
                console.error('Error initializing GCF:', error);
                this.error = this.$t('errorInitPayment');
            }
        },

        // Динамическая загрузка GCF скрипта
        loadGCFScript() {
            // Сначала проверим, загружен ли jQuery
            if (typeof jQuery === 'undefined') {
                const jqueryScript = document.createElement('script');
                jqueryScript.src = 'https://getcryptofast.com/js/jquery.min.js';
                jqueryScript.async = true;
                jqueryScript.onload = () => {
                    // После загрузки jQuery загружаем GCF
                    const gcfScript = document.createElement('script');
                    gcfScript.src = 'https://getcryptofast.com/js/gcf2.js';
                    gcfScript.async = true;
                    gcfScript.onload = this.createGCFInstance;
                    gcfScript.onerror = () => {
                        this.error = this.$t('errorLoadingPaymentSystem');
                    };
                    document.head.appendChild(gcfScript);
                };
                jqueryScript.onerror = () => {
                    this.error = this.$t('errorLoadingDependencies');
                };
                document.head.appendChild(jqueryScript);
            } else {
                // jQuery уже загружен, загружаем только GCF
                const script = document.createElement('script');
                script.src = 'https://getcryptofast.com/js/gcf2.js';
                script.async = true;
                script.onload = this.createGCFInstance;
                script.onerror = () => {
                    this.error = this.$t('errorLoadingPaymentSystem');
                };
                document.head.appendChild(script);
            }
        },

        // Создание экземпляра GCF
        createGCFInstance() {
            try {
                this.sp = new GCF();

                // Добавляем слушатель состояния
                this.sp.addEventListener('state', this.handleGCFState);

                // Добавляем слушатель ошибок
                this.sp.addEventListener('error', this.handleGCFError);
            } catch (error) {
                console.error('Error creating GCF instance:', error);
                this.error = this.$t('errorInitPayment');
            }
        },

        // Обработка изменений состояния GCF
        handleGCFState(event) {
            console.log('GCF state changed:', event);

            if (event.state === GCF.PaymentState.PAYRESULT) {
                // Платеж завершен, запрашиваем информацию о транзакции
                this.getTxnInfo();
            } else if (event.state === GCF.PaymentState.CHALLENGE) {
                // Появился 3DS challenge
                this.challengeUrl = event.message.url || event.message;
                this.loading = false; // Скрываем индикатор загрузки при показе 3DS
            }
        },

        // Обработка ошибок GCF
        handleGCFError(event) {
            console.error('GCF error:', event);
            this.error = `${this.$t('errorProcessingPayment').replace('{0}', event.message)}`;
            this.loading = false;
        },

        // Получение информации о транзакции
        async getTxnInfo() {
            try {
                const data = await this.sp.gettxninfo();
                if (!data) {
                    if (this.statusCheckCount < this.maxStatusChecks) {
                        // Увеличиваем счетчик проверок
                        this.statusCheckCount++;
                        // Пытаемся получить статус через 2 секунды
                        setTimeout(() => this.getTxnInfo(), 2000);
                        return;
                    } else {
                        // Если достигли максимального количества проверок, выводим ошибку
                        throw new Error(this.$t('transactionInfoNotAvailable'));
                    }
                }

                console.log('Transaction info:', data);

                // Проверяем статус транзакции
                if (data.status === GCF.TransactionStatus.DELIVERED) {
                    // Транзакция успешна
                    this.handlePaymentSuccess({
                        transactionId: this.sp.refid,
                        status: 'success',
                        paymentData: data
                    });
                } else if (data.status === GCF.TransactionStatus.FAILED) {
                    // Транзакция не удалась
                    this.error = `${this.$t('paymentFailed')}: ${data.statustext}`;
                    this.resetPaymentForm();
                } else {
                    // Транзакция в процессе, проверяем позже
                    setTimeout(() => this.getTxnInfo(), 5000);
                }
            } catch (error) {
                console.error('Error getting transaction info:', error);
                this.error = this.$t('errorGettingTransactionInfo');
            }
        },

        // Сброс формы оплаты
        resetPaymentForm() {
            if (this.sp) {
                this.sp.reset();
            }
            this.loading = false;
            this.challengeUrl = null;
        },

        // Форматирование номера карты
        formatCardNumber() {
            let value = this.cardNumber.replace(/\D/g, '');
            if (value.length > 16) value = value.slice(0, 16);

            // Форматирование с пробелами каждые 4 цифры
            let formattedValue = '';
            for (let i = 0; i < value.length; i++) {
                if (i > 0 && i % 4 === 0) {
                    formattedValue += ' ';
                }
                formattedValue += value[i];
            }
            this.cardNumber = formattedValue;
        },

        // Форматирование срока действия карты
        formatExpiryDate() {
            let value = this.expiryDate.replace(/\D/g, '');
            if (value.length > 4) value = value.slice(0, 4);

            if (value.length > 2) {
                this.expiryDate = value.slice(0, 2) + '/' + value.slice(2);
            } else {
                this.expiryDate = value;
            }
        },

        // Форматирование CVC кода
        formatCVC() {
            let value = this.cvc.replace(/\D/g, '');
            if (value.length > 4) value = value.slice(0, 4);
            this.cvc = value;
        },

        // Обработка сообщений от iframe
        handleIframeMessage(event) {
            console.log('Message received from iframe:', event.data);
            // GCF сам обрабатывает 3DS, но иногда может потребоваться перехватить сообщения
            if (event.data && event.data.challenge) {
                this.challengeUrl = event.data.challenge.url;
            } else if (event.data && event.data.status === 'completed') {
                // Платеж завершен
                this.getTxnInfo();
            }
        },

        // Обработка успешного платежа
        handlePaymentSuccess(result) {
            this.paymentCompleted = true;
            this.loading = false;
            this.challengeUrl = null;

            // Уведомляем о успешном платеже
            this.notifyPaymentSuccess(result)
                .then(() => {
                    // Генерируем событие успеха
                    this.$emit('payment-success', {
                        transactionId: result.transactionId,
                        invoiceId: this.invoiceId,
                        orderId: this.orderId,
                        paymentData: result
                    });
                })
                .catch(error => {
                    console.error('Error notifying about payment success:', error);
                    // Несмотря на ошибку уведомления, считаем платеж успешным
                    this.$emit('payment-success', {
                        transactionId: result.transactionId,
                        invoiceId: this.invoiceId,
                        orderId: this.orderId,
                        paymentData: result
                    });
                });
        },

        // Уведомление бэкенда о успешном платеже
        async notifyPaymentSuccess(result) {
            if (!result) {
                console.warn('No payment result to notify webhook about');
                return Promise.resolve();
            }

            let lang = store.state['vue-i18n-manager'].currentLanguage.translationKey;
            lang = lang === 'ru' ? 'rus' : 'eng';

            const webhookData = {
                orderId: this.orderId || this.invoiceId,
                status: 'success',
                paymentResult: result.paymentData || result,
                transactionReference: result.transactionId,
                invoiceType: this.invoiceType
            };

            const headers = {
                Authorization: this.uuid,
                'X-Lang-Header': lang,
                'Content-Type': 'application/json'
            };

            console.log('Notifying webhook about successful payment:', webhookData);

            try {
                const response = await axios.post(
                    `${process.env.VUE_APP_NEW_API_URL}/teido/webhook`,
                    webhookData,
                    { headers }
                );

                console.log('Webhook notification response:', response.data);
                return response.data;
            } catch (error) {
                console.error('Failed to notify webhook:', error);
                // Ошибка webhook не должна блокировать потоки пользователя
                return Promise.resolve();
            }
        },

        // Обработка платежа
        processPayment() {
            if (!this.isFormValid || this.loading) return;

            this.loading = true;
            this.error = null;

            try {
                // Проверяем, инициализирован ли GCF
                if (!this.sp) {
                    this.initGCF();
                    if (!this.sp) {
                        throw new Error(this.$t('paymentSystemNotInitialized'));
                    }
                }

                // Создаем или обновляем iframe для 3DS challenge
                this.setupChallengeIframe();

                // Инициализация платежа через GCF
                this.sp.init(
                    this.apiKey,
                    '#challenge3ds', // ID элемента для 3DS
                    this.amount,
                    this.customerId,
                    this.currency,
                    'USDC', // Криптовалюта (не используется для карточных платежей)
                    '137', // Blockchain ID (не используется для карточных платежей)
                    this.cardNumber.replace(/\s/g, ''),
                    this.cardholder,
                    this.expMonth,
                    this.expYear,
                    this.cvc,
                    '1000', // Product ID
                    this.description || `Payment for order ${this.orderId}`,
                    this.orderId || this.invoiceId,
                    this.externalid
                );

                // Начинаем процесс оплаты
                this.sp.pay();
                this.setupChallengeIframe();
            } catch (error) {
                console.error('Error processing payment:', error);
                this.error = `${this.$t('errorProcessingPayment').replace('{0}', error.message)}`;
                this.loading = false;
            }
        },

        // Подготовка iframe для 3DS challenge
        setupChallengeIframe() {
            // Проверяем, существует ли iframe для 3DS challenge
            let iframe = document.getElementById('challenge3ds');
            if (!iframe) {
                iframe = document.createElement('iframe');
                iframe.id = 'challenge3ds';
                iframe.style.display = 'none';
                iframe.title = '3DS Challenge';
                iframe.style.backgroundColor = 'white';

                // Find the payment-form-container element and append the iframe to it
                // instead of appending to document.body
                const container = document.querySelector('.payment-form-container');
                if (container) {
                    container.appendChild(iframe);
                } else {
                    // Fallback to document.body only if container isn't found
                    document.body.appendChild(iframe);
                }
            }
        }
    }
};